import { z } from 'zod'

export const V1_GOOGLE_RETURN_PATH = '/v1/onboarding/select-template'
export const V1_GOOGLE_STEP_KEY = 'v1_google_step_done'
export const V1_GOOGLE_RETURN_ENTRY_KEY = 'v1_google_return_entry_key'
export const V1_GOOGLE_SESSION_KEY = 'v1_onboarding_google_session'
const MAX_AGE_MS = 30 * 60_000
const sessionSchema = z.object({
  uid: z.string().min(1),
  nonce: z.string().uuid(),
  createdAt: z.number(),
  outcome: z.enum(['pending', 'connected', 'cancelled', 'failed']),
  returnEntryKey: z.string().optional(),
})

export const isOnboardingGoogleReturn = (path: string) =>
  path === V1_GOOGLE_RETURN_PATH || path.startsWith(`${V1_GOOGLE_RETURN_PATH}?`)

export function readOnboardingGoogleSession(storage: Pick<Storage, 'getItem'> = sessionStorage) {
  try {
    const result = sessionSchema.safeParse(
      JSON.parse(storage.getItem(V1_GOOGLE_SESSION_KEY) || 'null')
    )
    if (!result.success) return null
    const age = Date.now() - result.data.createdAt
    return age >= 0 && age <= MAX_AGE_MS ? result.data : null
  } catch {
    return null
  }
}

export function onboardingGoogleNonce(path: string) {
  const prefix = `${V1_GOOGLE_RETURN_PATH}?googleOnboarding=`
  if (!path.startsWith(prefix)) return null
  const nonce = path.slice(prefix.length)
  return z.string().uuid().safeParse(nonce).success ? nonce : null
}

/** Persist the initiating user before leaving; the return URL carries only the nonce. */
export const recordGoogleOAuthLaunch = (
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>,
  uid: string,
  navigationEntryKey: string | undefined
) => {
  const session = sessionSchema.parse({
    uid,
    nonce: crypto.randomUUID(),
    createdAt: Date.now(),
    outcome: 'pending',
    returnEntryKey: navigationEntryKey,
  })
  const serialized = JSON.stringify(session)
  storage.setItem(V1_GOOGLE_SESSION_KEY, serialized)
  if (storage.getItem(V1_GOOGLE_SESSION_KEY) !== serialized)
    throw new Error(
      'Unable to save Google authorization. Please enable browser storage and try again.'
    )
  if (navigationEntryKey) {
    storage.setItem(V1_GOOGLE_RETURN_ENTRY_KEY, navigationEntryKey)
  } else {
    storage.removeItem(V1_GOOGLE_RETURN_ENTRY_KEY)
  }
  return `${V1_GOOGLE_RETURN_PATH}?googleOnboarding=${session.nonce}`
}

export function finishOnboardingGoogleSession(
  uid: string,
  nonce: string,
  outcome: 'connected' | 'cancelled' | 'failed'
) {
  const session = readOnboardingGoogleSession()
  if (!session || session.uid !== uid || session.nonce !== nonce)
    throw new Error('Google authorization session changed. Return to setup and try again.')
  sessionStorage.setItem(V1_GOOGLE_SESSION_KEY, JSON.stringify({ ...session, outcome }))
}
